/**
 * File-type badge colors — mirrors the "FileTypeWraps" design-system component, which
 * ships one colored variant per file type.
 *
 * Those variants use a fixed per-type palette. Only the image/vector blue (#175ce5)
 * exists in our token system (= `--Color-Primary-500` = `bg-brand`), so it uses the
 * token class; the other colors are raw hexes in the design-system component
 * itself (not design tokens), reproduced here as arbitrary classes to match.
 */

import { SUPPORTED_IMAGE_FORMATS } from 'src/types/enum';

const BADGE = {
  red: 'tw:bg-[#d92d20]',
  orangeRed: 'tw:bg-[#e62e05]',
  orange: 'tw:bg-[#e04f16]',
  green: 'tw:bg-[#079455]',
  blue: 'tw:bg-[#155eef]',
  brand: 'tw:bg-brand',
  indigo: 'tw:bg-[#444ce7]',
  purple: 'tw:bg-[#6938ef]',
  magenta: 'tw:bg-[#ba24d5]',
  pink: 'tw:bg-[#dd2590]',
  slate: 'tw:bg-[#344054]',
  gray: 'tw:bg-[#475467]'
} as const;

/**
 * Maps a lower-cased file extension to the badge background class. Keyed by
 * extension string (not the `FILE_EXTENSIONS` enum) because the design covers
 * many types the enum doesn't (svg, zip, psd, …).
 */
export const FILE_TYPE_BADGE_COLOR_MAPPER: Record<string, string> = {
  pdf: BADGE.red,

  ppt: BADGE.orangeRed,
  pptx: BADGE.orangeRed,
  odp: BADGE.orangeRed,

  ai: BADGE.orange,

  csv: BADGE.green,
  xls: BADGE.green,
  xlsx: BADGE.green,
  ods: BADGE.green,

  doc: BADGE.blue,
  docx: BADGE.blue,
  odt: BADGE.blue,
  rtf: BADGE.blue,
  mp4: BADGE.blue,
  avi: BADGE.blue,
  mkv: BADGE.blue,
  mpeg: BADGE.blue,
  webm: BADGE.blue,
  psd: BADGE.blue,

  img: BADGE.brand,
  png: BADGE.brand,
  jpg: BADGE.brand,
  jpeg: BADGE.brand,
  webp: BADGE.brand,
  gif: BADGE.brand,
  tiff: BADGE.brand,
  svg: BADGE.brand,
  eps: BADGE.brand,
  fig: BADGE.brand,

  json: BADGE.indigo,
  html: BADGE.indigo,
  xml: BADGE.indigo,
  css: BADGE.indigo,
  js: BADGE.indigo,
  java: BADGE.indigo,
  sql: BADGE.indigo,
  rss: BADGE.indigo,
  exe: BADGE.indigo,
  dmg: BADGE.indigo,

  aep: BADGE.purple,

  indd: BADGE.magenta,

  mp3: BADGE.pink,
  wav: BADGE.pink,
  mpga: BADGE.pink,
  m4a: BADGE.pink,

  zip: BADGE.slate,
  rar: BADGE.slate,

  txt: BADGE.gray
};

const DEFAULT_BADGE_COLOR = BADGE.gray;

/** Lower-cased trailing extension of a filename, or undefined when none. */
export function inferFileExtension(name: string): string | undefined {
  const dot = name.lastIndexOf('.');
  if (dot < 0) {
    return undefined;
  }
  return name.slice(dot + 1).toLowerCase();
}

const IMAGE_EXTENSIONS = new Set(
  Object.values(SUPPORTED_IMAGE_FORMATS).map((mime) => mime.split('/')[1])
);

/**
 * Is this attachment an image?
 */
export function isImageAttachment(attachment: {
  name: string;
  extension?: string;
}): boolean {
  const extension = (
    attachment.extension ??
    inferFileExtension(attachment.name) ??
    ''
  ).toLowerCase();
  return IMAGE_EXTENSIONS.has(extension);
}

/** Tailwind bg-color class for the given extension (case-insensitive). */
export function getFileTypeBadgeColor(extension: string | undefined): string {
  if (!extension) {
    return DEFAULT_BADGE_COLOR;
  }
  return (
    FILE_TYPE_BADGE_COLOR_MAPPER[extension.toLowerCase()] ?? DEFAULT_BADGE_COLOR
  );
}
